import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useBlocker } from 'react-router-dom';
import apiService from '../services/apiService';
import Dialogo from '../components/Dialogo';
import { empresaInicial, lembrarEmpresa } from '../components/empresaLembrada';
import {
    MAX_NIVEIS, ehTemp, clonar, normalizarDaApi, porId as indexar, filhosDe as filhosEm, descendentes as descendentesEm,
    profundidade as profundidadeEm, adicionarGrupo, editarGrupo, moverGrupo, apagarGrupo,
    incluirTurma, tirarTurma, remapear, calcularMudancas, aplicar, programasDoGrupo,
} from './gruposDeTurmasRascunho';
import './GruposDeTurmas.css';

/**
 * Grupos de turmas.
 *
 * RASCUNHO LOCAL. Nada é gravado enquanto a pessoa edita: criar, renomear, cor, tags, mover, aninhar,
 * reordenar, apagar grupo, incluir e tirar turma só mudam o rascunho, que é o ESTADO DESEJADO COMPLETO.
 * As mudanças são o diff entre o rascunho e o último estado carregado do servidor (`base`); [Salvar]
 * mostra o resumo e, ao confirmar, aplica tudo em ordem segura (ver `aplicar` em gruposDeTurmasRascunho.js).
 * Se um passo falhar, recarrega o servidor e recalcula: o que não foi aplicado continua como não salvo.
 *
 * Com alterações não salvas: aviso nativo ao fechar/recarregar a aba (beforeunload) e modal próprio ao
 * navegar dentro do Admin ou voltar (useBlocker; o App usa data router).
 *
 * Duas colunas: TURMAS (a paleta) à esquerda, QUADRO de grupos à direita. Arrastar é HTML5 nativo (sem
 * biblioteca): turma -> grupo COPIA (N:N); alça do grupo -> outro cartão aninha; alça -> espaço entre
 * cartões reordena; "Nível principal" desaninha. Tudo tem caminho sem arrastar (WCAG 2.2 SC 2.5.7):
 * "+" na turma e menu do grupo (Mover para…, Subir, Descer…), com o resultado anunciado em aria-live.
 *
 * Escondido de propósito: nome do vendedor no ranking, mínimo de vendedores e ordem entre irmãos
 * (a ordem vem da POSIÇÃO). "Valem a partir de": um controle só, padrão hoje, nunca antes de hoje.
 */

const PALETA = [
    { cor: '#2F6B52', nome: 'Verde' },
    { cor: '#A24A12', nome: 'Laranja' },
    { cor: '#1F5FA6', nome: 'Azul' },
    { cor: '#7A3BB8', nome: 'Roxo' },
    { cor: '#B3261E', nome: 'Vermelho' },
    { cor: '#0F7B8A', nome: 'Turquesa' },
    { cor: '#8A6D00', nome: 'Ocre' },
    { cor: '#C2185B', nome: 'Rosa' },
    { cor: '#4B5D16', nome: 'Oliva' },
    { cor: '#5B6770', nome: 'Cinza' },
];
const COR_PADRAO = '#5B6770';

const ROTULO_MOVIMENTO = {
    group_created: 'Grupo criado',
    group_moved: 'Grupo movido',
    group_removed: 'Grupo apagado',
    member_added: 'Turma incluída',
    member_removed: 'Turma retirada',
};

const TEXTO_DATA = 'As mudanças só são gravadas quando você clica em Salvar, e valem a partir desta data. Ela fica no histórico. Não dá para escolher uma data anterior a hoje, para o passado não ser alterado.';
const TEXTO_TAGS = 'Escreva as tags separadas por vírgula. Ao salvar, ficam em minúsculas e sem acento, espaços viram "_" e tags repetidas são unidas (até 8 tags, 40 caracteres cada).';
const TEXTO_QUADRO = 'Arraste uma turma da lista para um grupo para colocá-la nele; ela continua na lista, porque uma turma pode estar em vários grupos. Arraste a alça (os pontinhos à esquerda do nome) de um grupo para cima de outro para aninhar, ou para o espaço entre grupos para reordenar. Sem arrastar: use o botão + da turma e o menu de três pontos do grupo. A ordem dos grupos do mesmo nível é a posição em que você os solta. Nada é gravado até você clicar em Salvar.';

const TEXTO_VISIB = 'Vale para o ranking de VENDEDORES dentro deste grupo e dos grupos abaixo dele. O vendedor sempre vê o próprio nome. Se ficar em "Herdar", vale o do grupo pai; o padrão da rede é só iniciais. "Anônimo" mostra Vendedor 1, Vendedor 2… O admin sempre vê tudo.';
const TEXTO_MINIMO = 'Turma ou grupo com menos vendedores ativos do que este número aparece no ranking só com a posição, sem o número, para não expor a nota de uma pessoa. Vazio herda do grupo pai; o padrão da rede é 2.';
const TEXTO_PROGRAMAS = 'Por padrão um grupo só aceita turmas do mesmo programa, para o ranking comparar o que é comparável. Ligue só se quiser comparar turmas de programas diferentes (as fases podem ser diferentes).';
const TEXTO_AVISO_MISTURA = 'Este grupo tem turmas de programas diferentes mas a opção "Permitir programas diferentes" está desligada. Ajuste os programas ou ligue a opção.';
const TEXTO_PROGRAMAS_DIFERENTES = 'Este grupo tem turmas de programas diferentes e a opção "Permitir programas diferentes" está ligada: o ranking compara turmas que podem estar em fases diferentes.';
const TEXTO_FASES = 'Por padrão o ranking do grupo compara só quem está na mesma fase que você (fase = tipo da fase e posição na trilha, igual entre programas). Ligue para incluir todos, mesmo em fases diferentes: janelas e metas podem ser diferentes e a comparação pode não ser equivalente.';
const ROTULO_VISIB = { full: 'nome completo', initials: 'só iniciais', anonymous: 'anônimo' };
const ROTULO_ALTERACAO = { nome: 'nome', cor: 'cor', tags: 'tags', visibilidade: 'nome do vendedor no ranking', minimo: 'mínimo de vendedores', programas: 'programas diferentes', fases: 'fases diferentes' };
const textoVisib = (v) => (v ? ROTULO_VISIB[v] : 'herdar');
const textoMinimo = (m) => (m ? String(m) : 'herdar');

const pad2 = (n) => String(n).padStart(2, '0');
const hojeISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const dataBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/* ------------------------------------------------------------------ */
/* Pequenos componentes                                                */
/* ------------------------------------------------------------------ */

/** Ícone ⓘ com texto em popover: abre por clique ou teclado, fecha com Esc ou clique fora. */
function Info({ texto, rotulo = 'Mais informações' }) {
    const [aberto, setAberto] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!aberto) return undefined;
        const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);
    return (
        <span className="gt-info" ref={ref}
            onKeyDown={(e) => { if (e.key === 'Escape' && aberto) { e.stopPropagation(); setAberto(false); } }}>
            <button type="button" className="gt-info-btn" aria-label={rotulo} aria-expanded={aberto}
                onClick={() => setAberto((a) => !a)}>i</button>
            {aberto && <span className="gt-info-pop" role="note">{texto}</span>}
        </span>
    );
}

const IconeAlca = () => (
    <svg className="gt-svg" width="12" height="18" viewBox="0 0 12 18" aria-hidden="true" focusable="false">
        {[3, 9, 15].map((y) => [3, 9].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" fill="currentColor" />))}
    </svg>
);
const IconeMenu = () => (
    <svg className="gt-svg" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        {[3, 8, 13].map((x) => <circle key={x} cx={x} cy="8" r="1.7" fill="currentColor" />)}
    </svg>
);

/** Menu de três pontos: botões em lista; Esc e clique fora fecham, Esc devolve o foco ao botão. */
function Menu({ rotulo, itens }) {
    const [aberto, setAberto] = useState(false);
    const ref = useRef(null);
    const btnRef = useRef(null);
    useEffect(() => {
        if (!aberto) return undefined;
        const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);
    return (
        <span className="gt-menu" ref={ref}
            onKeyDown={(e) => {
                if (e.key === 'Escape' && aberto) { e.stopPropagation(); setAberto(false); btnRef.current?.focus(); }
            }}>
            <button type="button" className="gt-icone-btn" ref={btnRef} aria-label={rotulo}
                aria-haspopup="menu" aria-expanded={aberto} onClick={() => setAberto((a) => !a)}><IconeMenu /></button>
            {aberto && (
                <ul className="gt-menu-lista" role="menu">
                    {itens.filter(Boolean).map((it) => (
                        <li key={it.label} role="none">
                            <button type="button" role="menuitem" disabled={it.desabilitado}
                                className={it.perigo ? 'gt-menu-perigo' : ''}
                                onClick={() => { setAberto(false); it.acao(); }}>{it.label}</button>
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
}

/** Programa da turma: etiqueta pequena, com uma cor estável por programa (só para enxergar a diferença). */
const corDoPrograma = (id) => `hsl(${(Number(id) * 137) % 360}, 62%, 90%)`;
const ProgramaTag = ({ programa }) => (programa ? (
    <span className="gt-prog" style={{ background: corDoPrograma(programa.id) }} title={`Programa: ${programa.name}`}>
        {programa.name}
    </span>
) : null);

const Bolinha = ({ cor, titulo }) => (
    <span className="gt-bolinha" style={{ background: cor || COR_PADRAO }} title={titulo} aria-hidden="true" />
);

/* ------------------------------------------------------------------ */
/* Página                                                              */
/* ------------------------------------------------------------------ */

function GruposDeTurmas() {
    const [params] = useSearchParams();
    const [toast, setToast] = useState(null);
    const [aviso, setAviso] = useState('');            // região aria-live
    const toastTimer = useRef(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setAviso(msg);
        // o timer do aviso anterior não pode apagar o novo
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 5000);
    }, []);
    useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

    const [companies, setCompanies] = useState([]);
    const [subdomain, setSubdomain] = useState(() => empresaInicial(params.get('subdomain')));
    const [base, setBase] = useState([]);              // último estado do servidor
    const [rasc, setRasc] = useState([]);              // rascunho = estado desejado completo
    const [turmas, setTurmas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [progresso, setProgresso] = useState('');

    const [vigencia, setVigencia] = useState(hojeISO());
    const [busca, setBusca] = useState('');
    const [soSemGrupo, setSoSemGrupo] = useState(false);
    const [dlg, setDlg] = useState(null);              // diálogo aberto
    const [arrastando, setArrastando] = useState(null); // { tipo: 'turma'|'grupo', id }
    const [alvo, setAlvo] = useState(null);            // alvo de soltar em destaque
    const [falha, setFalha] = useState(null);          // { rotulo, mensagem } do último salvar que falhou
    const arrastoRef = useRef(null);
    const saindoRef = useRef(false);
    const rascRef = useRef([]);                    // rascunho atual, para validar uma edição ANTES de aplicá-la

    useEffect(() => {
        (async () => {
            try {
                const lista = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(lista) ? lista : []);
                setSubdomain((atual) => atual || (lista.length ? lista[0].subdomain : ''));
            } catch (e) {
                notify('Não foi possível carregar as empresas', 'error');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Lê o servidor. Devolve o estado normalizado; quem chama decide se zera o rascunho. */
    const lerServidor = useCallback(async () => {
        const [g, t] = await Promise.all([
            apiService.cohortGroupList({ subdomain, include_cohorts: true }),
            apiService.phaseCohortsList({ subdomain }),
        ]);
        return { grupos: normalizarDaApi(g?.data?.groups), turmas: t?.data?.cohorts || [] };
    }, [subdomain]);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        lembrarEmpresa(subdomain);
        setLoading(true);
        try {
            const s = await lerServidor();
            setBase(s.grupos);
            setRasc(clonar(s.grupos));
            setTurmas(s.turmas);
            setFalha(null);
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao carregar os grupos', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, lerServidor, notify]);

    useEffect(() => { setDlg(null); carregar(); }, [carregar]);

    /* ---------------- derivados ---------------- */
    rascRef.current = rasc;
    const mud = useMemo(() => calcularMudancas(base, rasc), [base, rasc]);
    const sujo = mud.total > 0;
    const porId = useMemo(() => indexar(rasc), [rasc]);
    const basePorId = useMemo(() => indexar(base), [base]);
    const turmaPorId = useMemo(() => Object.fromEntries(turmas.map((t) => [t.id, t])), [turmas]);
    const nomeTurma = (id) => turmaPorId[id]?.name || `turma ${id}`;
    const programaDe = useCallback((cid) => turmaPorId[cid]?.program?.id ?? null, [turmaPorId]);
    const filhosDe = useCallback((paiId) => filhosEm(rasc, paiId), [rasc]);

    // selos: o que mudou em cada grupo e em cada chip
    const marcas = useMemo(() => {
        const alterados = {};
        mud.editados.forEach((e) => {
            const o = [];
            if (e.quais.length) o.push(e.quais.map((q) => ROTULO_ALTERACAO[q] || q).join(', '));
            if (e.mudouPai) o.push('posição na árvore');
            if (e.mudouOrdem) o.push('ordem');
            alterados[e.g.id] = o.join(' · ');
        });
        const incl = new Set(mud.incluidas.map((x) => `${x.grupo.id}:${x.cohort_id}`));
        const ret = {};
        mud.retiradas.forEach((x) => { (ret[x.grupo.id] = ret[x.grupo.id] || []).push(x.cohort_id); });
        return { alterados, incl, ret };
    }, [mud]);

    const gruposDaTurma = useMemo(() => {
        const m = {};
        rasc.forEach((g) => g.cohorts.forEach((c) => { (m[c] = m[c] || []).push(g); }));
        return m;
    }, [rasc]);

    const proximaCor = useMemo(() => {
        const usadas = new Set(rasc.map((g) => (g.color || '').toUpperCase()));
        return (PALETA.find((p) => !usadas.has(p.cor)) || PALETA[rasc.length % PALETA.length]).cor;
    }, [rasc]);

    const turmasVisiveis = useMemo(() => {
        const q = semAcento(busca.trim());
        return turmas.filter((t) => (!q || semAcento(t.name).includes(q) || semAcento(t.store?.name).includes(q))
            && (!soSemGrupo || !(gruposDaTurma[t.id] || []).length));
    }, [turmas, busca, soSemGrupo, gruposDaTurma]);

    /* ---------------- guarda de saída ---------------- */
    useEffect(() => {
        if (!sujo) return undefined;
        const aoSair = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
        window.addEventListener('beforeunload', aoSair);
        return () => window.removeEventListener('beforeunload', aoSair);
    }, [sujo]);

    const blocker = useBlocker(({ currentLocation, nextLocation }) => sujo && !saindoRef.current
        && (currentLocation.pathname !== nextLocation.pathname));

    /* ---------------- edições locais ---------------- */
    const vig = () => {
        const hoje = hojeISO();
        if (!vigencia || vigencia < hoje) { setVigencia(hoje); return hoje; }
        return vigencia;
    };

    const onDataMudou = (v) => {
        if (v && v < hojeISO()) {
            setVigencia(hojeISO());
            notify('A data não pode ser anterior a hoje', 'error');
            return;
        }
        setVigencia(v || hojeISO());
    };

    /**
     * Roda uma edição local. A função é executada AGORA (não dentro do atualizador do React, que roda
     * depois e deixaria o erro escapar do try/catch): erro de regra vira aviso e o rascunho não muda.
     */
    const aplicarLocal = (fn) => {
        const novo = fn(rascRef.current);
        rascRef.current = novo;
        setRasc(novo);
    };

    const editar = (fn, mensagem) => {
        try {
            aplicarLocal(fn);
            if (mensagem) setAviso(mensagem);
            return true;
        } catch (e) {
            notify(e.message, 'error');
            return false;
        }
    };

    const incluir = (cohortId, groupId) => {
        const g = porId[groupId];
        const t = turmaPorId[cohortId];
        if (!g || !t) return;
        if (g.cohorts.includes(cohortId)) { notify(`A turma "${t.name}" já está em "${g.name}"`, 'error'); return; }
        editar((r) => incluirTurma(r, groupId, cohortId, programaDe), `Turma "${t.name}" colocada em "${g.name}" (não salvo)`);
    };

    const tirar = (cohortId, groupId) => {
        editar((r) => tirarTurma(r, groupId, cohortId), `Turma "${nomeTurma(cohortId)}" retirada de "${porId[groupId]?.name}" (não salvo)`);
    };

    const mover = (id, novoPai, indice = null) => {
        const g = porId[id];
        if (!g) return;
        const antes = g.parent_id;
        const ok = editar((r) => moverGrupo(r, id, novoPai, indice, programaDe),
            novoPai !== antes
                ? `Grupo "${g.name}" movido para ${novoPai == null ? 'o nível principal' : `dentro de "${porId[novoPai]?.name}"`} (não salvo)`
                : `Grupo "${g.name}" reordenado (não salvo)`);
        return ok;
    };

    const salvarGrupoLocal = () => {
        const f = dlg.form;
        if (!(f.name || '').trim()) { notify('Dê um nome ao grupo', 'error'); return; }
        try {
            if (f.id) {
                aplicarLocal((r) => editarGrupo(r, f.id, { name: f.name, color: f.color, tags: f.tags,
                    salesman_visibility: f.salesman_visibility, min_active: f.min_active,
                    allow_mixed_programs: !!f.allow_mixed_programs, allow_mixed_phases: !!f.allow_mixed_phases }, programaDe));
            } else {
                aplicarLocal((r) => adicionarGrupo(r, { name: f.name, color: f.color, tags: f.tags, parent_id: f.parent_id,
                    salesman_visibility: f.salesman_visibility, min_active: f.min_active,
                    allow_mixed_programs: !!f.allow_mixed_programs, allow_mixed_phases: !!f.allow_mixed_phases }).rasc);
            }
            setAviso(f.id ? 'Grupo alterado (não salvo)' : `Grupo "${f.name.trim()}" criado (não salvo)`);
            setDlg(null);
        } catch (e) { notify(e.message, 'error'); }
    };

    const apagarLocal = () => {
        const g = dlg.grupo;
        if (editar((r) => apagarGrupo(r, g.id), `Grupo "${g.name}" apagado (não salvo)`)) setDlg(null);
    };

    const abrirHistorico = async (g) => {
        try {
            const r = await apiService.cohortGroupMovements({ subdomain, group_id: g.id });
            setDlg({ tipo: 'historico', grupo: g, movimentos: r?.data?.movements || [] });
        } catch (e) { notify(e?.response?.data?.detail || 'Erro ao carregar o histórico', 'error'); }
    };

    const novoGrupo = (paiId = null) => setDlg({
        tipo: 'grupo', form: { id: null, name: '', color: proximaCor, tags: '', parent_id: paiId,
            salesman_visibility: '', min_active: '', allow_mixed_programs: false, avancado: false } });

    const editarGrupoDlg = (g) => setDlg({
        tipo: 'grupo', form: { id: g.id, name: g.name, color: (g.color || COR_PADRAO).toUpperCase(), tags: g.tags.join(', '), parent_id: g.parent_id,
            salesman_visibility: g.salesman_visibility || '', min_active: g.min_active ? String(g.min_active) : '',
            allow_mixed_programs: !!g.allow_mixed_programs, allow_mixed_phases: !!g.allow_mixed_phases,
            avancado: !!(g.salesman_visibility || g.min_active || g.allow_mixed_programs || g.allow_mixed_phases) } });

    /* ---------------- salvar / descartar ---------------- */
    const abrirResumo = () => {
        if (!sujo) return;
        vig();
        setDlg({ tipo: 'resumo' });
    };

    const confirmarSalvar = async () => {
        const dia = vig();
        setOcupado(true);
        setFalha(null);
        let resultado;
        try {
            resultado = await aplicar({
                api: apiService, subdomain, base, rasc, vigencia: dia, turmas, aoPasso: setProgresso,
            });
        } catch (e) {
            resultado = { ok: false, mapa: {}, aplicados: 0, falha: { rotulo: 'Salvar', mensagem: e?.message || 'Erro inesperado' } };
        }
        setProgresso('');
        try {
            const s = await lerServidor();       // o que valeu está no servidor; recalcula a partir dele
            setBase(s.grupos);
            setTurmas(s.turmas);
            if (resultado.ok) {
                setRasc(clonar(s.grupos));
                notify('Alterações salvas', 'success');
            } else {
                setRasc((atual) => remapear(atual, resultado.mapa));
                setFalha(resultado.falha);
                notify(`Não foi possível salvar: ${resultado.falha.mensagem}`, 'error');
            }
        } catch (e) {
            setFalha({ rotulo: 'Recarregar', mensagem: e?.response?.data?.detail || 'Erro ao recarregar os grupos' });
        } finally {
            setOcupado(false);
            setDlg(null);
        }
    };

    const descartar = () => {
        setRasc(clonar(base));
        setFalha(null);
        setDlg(null);
        notify('Alterações descartadas', 'info');
    };

    const trocarEmpresa = (valor) => {
        if (sujo) setDlg({ tipo: 'trocar-empresa', valor });
        else setSubdomain(valor);
    };

    /* ---------------- arrastar ---------------- */
    const iniciarArrasto = (e, tipo, id) => {
        arrastoRef.current = { tipo, id };
        setArrastando({ tipo, id });
        e.dataTransfer.effectAllowed = tipo === 'turma' ? 'copy' : 'move';
        e.dataTransfer.setData('text/plain', `${tipo}:${id}`);
    };
    const terminarArrasto = () => { arrastoRef.current = null; setArrastando(null); setAlvo(null); };

    const grupoPodeReceberGrupo = (alvoId) => {
        const a = arrastoRef.current;
        if (!a || a.tipo !== 'grupo') return false;
        return alvoId == null ? true : (alvoId !== a.id && !descendentesEm(rasc, a.id).has(alvoId));
    };

    /* ---------------- render ---------------- */
    const raizes = filhosDe(null);

    const renderLacuna = (pai, indice) => {
        const chave = `lacuna:${pai}:${indice}`;
        const ativa = arrastando?.tipo === 'grupo';
        return (
            <div className={`gt-lacuna${ativa ? ' gt-lacuna-ativa' : ''}${alvo === chave ? ' gt-lacuna-sobre' : ''}`}
                onDragOver={(e) => {
                    if (!grupoPodeReceberGrupo(pai)) return;
                    e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setAlvo(chave);
                }}
                onDragLeave={() => setAlvo((a) => (a === chave ? null : a))}
                onDrop={(e) => {
                    if (!grupoPodeReceberGrupo(pai)) return;
                    e.preventDefault(); e.stopPropagation();
                    const id = arrastoRef.current.id; terminarArrasto(); mover(id, pai, indice);
                }} />
        );
    };

    const renderCartao = (g, indiceNoPai, totalIrmaos) => {
        const filhos = filhosDe(g.id);
        const cor = (g.color || COR_PADRAO).toUpperCase();
        const chave = `g:${g.id}`;
        const sobre = alvo === chave;
        const novo = ehTemp(g.id);
        const alterado = !novo && marcas.alterados[g.id] !== undefined;
        const retiradas = marcas.ret[g.id] || [];
        const mistura = programasDoGrupo(rasc, g.id, programaDe).size > 1;
        const avisoMistura = mistura && !g.allow_mixed_programs;
        const neutroMistura = mistura && g.allow_mixed_programs;
        return (
            <section className={`gt-cartao${sobre ? ' gt-cartao-sobre' : ''}${arrastando?.tipo === 'grupo' && arrastando.id === g.id ? ' gt-cartao-arrastado' : ''}${novo ? ' gt-cartao-novo' : ''}${alterado ? ' gt-cartao-alterado' : ''}`}
                style={{ '--c': cor }} aria-label={`Grupo ${g.name}${novo ? ' (novo)' : alterado ? ' (alterado)' : ''}`}
                onDragOver={(e) => {
                    const a = arrastoRef.current;
                    if (!a) return;
                    if (a.tipo === 'turma' || grupoPodeReceberGrupo(g.id)) {
                        e.preventDefault(); e.stopPropagation();
                        e.dataTransfer.dropEffect = a.tipo === 'turma' ? 'copy' : 'move';
                        setAlvo(chave);
                    }
                }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setAlvo((a) => (a === chave ? null : a)); }}
                onDrop={(e) => {
                    const a = arrastoRef.current;
                    if (!a) return;
                    const aceita = a.tipo === 'turma' || grupoPodeReceberGrupo(g.id);
                    if (!aceita) return;
                    e.preventDefault(); e.stopPropagation();
                    terminarArrasto();
                    if (a.tipo === 'turma') incluir(a.id, g.id);
                    else mover(a.id, g.id, null);
                }}>
                <header className="gt-cartao-topo" draggable
                    onDragStart={(e) => iniciarArrasto(e, 'grupo', g.id)} onDragEnd={terminarArrasto}>
                    <span className="gt-alca" aria-hidden="true" title="Arraste para mover o grupo"><IconeAlca /></span>
                    <h3 className="gt-cartao-nome">{g.name}</h3>
                    {novo && <span className="gt-selo gt-selo-novo">novo</span>}
                    {alterado && <span className="gt-selo gt-selo-alterado" title={`Alterado: ${marcas.alterados[g.id]}`}>alterado</span>}
                    <span className="gt-contagem">{g.cohorts.length} {g.cohorts.length === 1 ? 'turma' : 'turmas'}</span>
                    <Menu rotulo={`Ações do grupo ${g.name}`} itens={[
                        { label: 'Renomear, cor e tags…', acao: () => editarGrupoDlg(g) },
                        { label: 'Novo subgrupo…', acao: () => novoGrupo(g.id), desabilitado: profundidadeEm(rasc, g.id) >= MAX_NIVEIS },
                        { label: 'Mover para…', acao: () => setDlg({ tipo: 'mover', grupo: g, destino: g.parent_id == null ? '' : String(g.parent_id) }) },
                        { label: 'Subir', acao: () => mover(g.id, g.parent_id == null ? null : g.parent_id, indiceNoPai - 1), desabilitado: indiceNoPai === 0 },
                        { label: 'Descer', acao: () => mover(g.id, g.parent_id == null ? null : g.parent_id, indiceNoPai + 1), desabilitado: indiceNoPai >= totalIrmaos - 1 },
                        { label: 'Histórico', acao: () => abrirHistorico(g), desabilitado: novo },
                        { label: 'Apagar…', acao: () => setDlg({ tipo: 'apagar', grupo: g }), perigo: true },
                    ]} />
                </header>

                {(g.tags.length > 0 || g.salesman_visibility || g.min_active || g.allow_mixed_phases || avisoMistura || neutroMistura) && (
                    <ul className="gt-tags" aria-label="Tags e opções do grupo">
                        {avisoMistura && (
                            <li className="gt-aviso-chip">
                                <span>mistura programas</span>
                                <Info texto={TEXTO_AVISO_MISTURA} rotulo="Sobre a mistura de programas" />
                            </li>
                        )}
                        {neutroMistura && (
                            <li className="gt-opcao gt-opcao-info">
                                <span>programas diferentes</span>
                                <Info texto={TEXTO_PROGRAMAS_DIFERENTES} rotulo="Sobre programas diferentes no grupo" />
                            </li>
                        )}
                        {g.tags.map((t) => <li key={t}>{t}</li>)}
                        {g.salesman_visibility && (
                            <li className="gt-opcao" tabIndex={0}
                                title={`Nome do vendedor no ranking: ${ROTULO_VISIB[g.salesman_visibility]}. Vale para este grupo e os abaixo.`}
                                aria-label={`Nome do vendedor no ranking: ${ROTULO_VISIB[g.salesman_visibility]}`}>
                                {ROTULO_VISIB[g.salesman_visibility]}
                            </li>
                        )}
                        {g.allow_mixed_phases && (
                            <li className="gt-opcao" tabIndex={0}
                                title="Fases diferentes permitidas: o ranking deste grupo inclui todos, mesmo em fases diferentes (a comparação pode não ser equivalente)."
                                aria-label="Fases diferentes permitidas">
                                fases diferentes
                            </li>
                        )}
                        {g.min_active && (
                            <li className="gt-opcao" tabIndex={0}
                                title={`Mínimo de vendedores para mostrar o número: ${g.min_active}. Abaixo disso o ranking mostra só a posição.`}
                                aria-label={`Mínimo de vendedores para mostrar o número: ${g.min_active}`}>
                                mín. {g.min_active}
                            </li>
                        )}
                    </ul>
                )}

                <div className="gt-turmas-do-grupo">
                    {g.cohorts.length === 0 && retiradas.length === 0 && filhos.length === 0 && (
                        <p className="gt-vazio-cartao">Arraste turmas aqui</p>
                    )}
                    {g.cohorts.map((c) => (
                        <span className={`gt-chip gt-chip-no-grupo${marcas.incl.has(`${g.id}:${c}`) ? ' gt-chip-novo' : ''}`} key={c}>
                            {marcas.incl.has(`${g.id}:${c}`) && <span className="gt-selo gt-selo-novo">nova</span>}
                            <span className="gt-chip-nome">{nomeTurma(c)}</span>
                            <ProgramaTag programa={turmaPorId[c]?.program} />
                            <button type="button" className="gt-chip-x" disabled={ocupado}
                                aria-label={`Tirar a turma ${nomeTurma(c)} do grupo ${g.name}`}
                                onClick={() => tirar(c, g.id)}>×</button>
                        </span>
                    ))}
                    {retiradas.map((c) => (
                        <span className="gt-chip gt-chip-no-grupo gt-chip-retirada" key={`r${c}`}>
                            <span className="gt-selo gt-selo-retirada">retirada</span>
                            <span className="gt-chip-nome">{nomeTurma(c)}</span>
                            <ProgramaTag programa={turmaPorId[c]?.program} />
                            <button type="button" className="gt-chip-desfazer" disabled={ocupado}
                                aria-label={`Desfazer: manter a turma ${nomeTurma(c)} no grupo ${g.name}`}
                                onClick={() => incluir(c, g.id)}>desfazer</button>
                        </span>
                    ))}
                </div>

                {filhos.length > 0 && (
                    <div className="gt-aninhados">
                        {renderLacuna(g.id, 0)}
                        {filhos.map((f, i) => (
                            <React.Fragment key={f.id}>
                                {renderCartao(f, i, filhos.length)}
                                {renderLacuna(g.id, i + 1)}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </section>
        );
    };

    /* ----- resumo de confirmação, em português, com nomes ----- */
    const nomeG = (id) => (porId[id] || basePorId[id] || {}).name || `grupo ${id}`;
    const onde = (id) => (id == null ? 'no nível principal' : `dentro de "${nomeG(id)}"`);
    const origem = (id) => (id == null ? 'do nível principal' : `de "${nomeG(id)}"`);
    const destino = (id) => (id == null ? 'para o nível principal' : `para "${nomeG(id)}"`);
    const Resumo = () => (
        <div className="gt-resumo">
            {mud.novos.length > 0 && (
                <section>
                    <h4>Grupos novos ({mud.novos.length})</h4>
                    <ul>{mud.novos.map((g) => (
                        <li key={g.id}><Bolinha cor={g.color} /> <strong>{g.name}</strong> — {onde(g.parent_id)}{g.salesman_visibility && <> · nome do vendedor: {textoVisib(g.salesman_visibility)}</>}{g.min_active && <> · mínimo de vendedores: {g.min_active}</>}{g.allow_mixed_programs && <> · programas diferentes: permitido</>}{g.allow_mixed_phases && <> · fases diferentes: permitido</>}</li>
                    ))}</ul>
                </section>
            )}
            {mud.editados.some((e) => e.quais.some((q) => ['nome', 'cor', 'tags'].includes(q))) && (
                <section>
                    <h4>Grupos renomeados / cor / tags</h4>
                    <ul>{mud.editados.filter((e) => e.quais.some((q) => ['nome', 'cor', 'tags'].includes(q))).map((e) => (
                        <li key={e.g.id}>
                            <strong>{e.antes.name}</strong>
                            {e.quais.includes('nome') && <> → renomeado para <strong>{e.g.name}</strong></>}
                            {e.quais.includes('cor') && <> · cor alterada <Bolinha cor={e.antes.color} /> → <Bolinha cor={e.g.color} /></>}
                            {e.quais.includes('tags') && <> · tags: {e.antes.tags.join(', ') || 'nenhuma'} → {e.g.tags.join(', ') || 'nenhuma'}</>}
                        </li>
                    ))}</ul>
                </section>
            )}
            {mud.editados.some((e) => e.quais.some((q) => ['visibilidade', 'minimo', 'programas', 'fases'].includes(q))) && (
                <section>
                    <h4>Opções avançadas</h4>
                    <ul>{mud.editados.filter((e) => e.quais.some((q) => ['visibilidade', 'minimo', 'programas', 'fases'].includes(q))).map((e) => (
                        <li key={e.g.id}>
                            <strong>{e.g.name}</strong>
                            {e.quais.includes('visibilidade') && <> · Nome do vendedor: {textoVisib(e.antes.salesman_visibility)} → {textoVisib(e.g.salesman_visibility)}</>}
                            {e.quais.includes('minimo') && <> · Mínimo de vendedores: {textoMinimo(e.antes.min_active)} → {textoMinimo(e.g.min_active)}</>}
                            {e.quais.includes('programas') && <> · Programas diferentes: {e.antes.allow_mixed_programs ? 'permitido' : 'não permitido'} → {e.g.allow_mixed_programs ? 'permitido' : 'não permitido'}</>}
                            {e.quais.includes('fases') && <> · Fases diferentes: {e.antes.allow_mixed_phases ? 'permitido' : 'não permitido'} → {e.g.allow_mixed_phases ? 'permitido' : 'não permitido'}</>}
                        </li>
                    ))}</ul>
                </section>
            )}
            {mud.editados.some((e) => e.mudouPai) && (
                <section>
                    <h4>Grupos movidos</h4>
                    <ul>{mud.editados.filter((e) => e.mudouPai).map((e) => (
                        <li key={e.g.id}><strong>{e.g.name}</strong>: {origem(e.antes.parent_id)} {destino(e.g.parent_id)}</li>
                    ))}</ul>
                </section>
            )}
            {mud.editados.some((e) => e.mudouOrdem) && (
                <section>
                    <h4>Ordem alterada</h4>
                    <ul>{mud.editados.filter((e) => e.mudouOrdem).map((e) => (
                        <li key={e.g.id}><strong>{e.g.name}</strong><> ({onde(e.g.parent_id)})</></li>
                    ))}</ul>
                </section>
            )}
            {mud.incluidas.length > 0 && (
                <section>
                    <h4>Turmas incluídas ({mud.incluidas.length})</h4>
                    <ul>{mud.incluidas.map((x) => (
                        <li key={`${x.grupo.id}:${x.cohort_id}`}>{nomeTurma(x.cohort_id)} → <strong>{x.grupo.name}</strong></li>
                    ))}</ul>
                </section>
            )}
            {mud.retiradas.length > 0 && (
                <section>
                    <h4>Turmas retiradas ({mud.retiradas.length})</h4>
                    <ul>{mud.retiradas.map((x) => (
                        <li key={`${x.grupo.id}:${x.cohort_id}`}>{nomeTurma(x.cohort_id)} ← <strong>{x.grupo.name}</strong></li>
                    ))}</ul>
                </section>
            )}
            {mud.apagados.length > 0 && (
                <section>
                    <h4>Grupos apagados ({mud.apagados.length})</h4>
                    <ul>{mud.apagados.map((g) => (
                        <li key={g.id}><strong>{g.name}</strong>{g.cohorts.length > 0 && <> — as {g.cohorts.length} turma(s) dele saem do grupo e continuam existindo</>}</li>
                    ))}</ul>
                </section>
            )}
            <p className="gt-resumo-vigencia">Valem a partir de <strong>{dataBR(vigencia)}</strong>.</p>
        </div>
    );

    return (
        <div className={`gt-page${sujo ? ' gt-page-com-barra' : ''}${ocupado ? ' gt-travado' : ''}`}>
            {toast && <div className={`gt-toast gt-toast-${toast.type}`} role="status">{toast.msg}</div>}
            <div className="gt-sr" aria-live="polite" role="status">{aviso}</div>

            <div className="gt-header">
                <div>
                    <h1>Grupos de turmas</h1>
                    <label className="gt-campo-inline">
                        Empresa
                        <select value={subdomain} onChange={(e) => trocarEmpresa(e.target.value)}>
                            {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name || c.subdomain}</option>)}
                        </select>
                    </label>
                </div>
                <div className="gt-header-direita">
                    <span className="gt-campo-inline">
                        <label htmlFor="gt-vigencia">As mudanças valem a partir de</label>
                        <Info texto={TEXTO_DATA} rotulo="Sobre a data de vigência" />
                    </span>
                    <input id="gt-vigencia" type="date" value={vigencia} min={hojeISO()}
                        onChange={(e) => onDataMudou(e.target.value)} />
                    <button type="button" className="gt-btn" onClick={() => novoGrupo(null)} disabled={!subdomain}>
                        + Novo grupo
                    </button>
                    <button type="button" className="gt-btn gt-btn-primario" onClick={abrirResumo} disabled={!sujo || ocupado}>
                        Salvar
                    </button>
                </div>
            </div>

            {falha && (
                <div className="gt-falha" role="alert">
                    <strong>Não foi possível salvar tudo.</strong>
                    <span>Passo que falhou: {falha.rotulo}.</span>
                    <span>Motivo: {falha.mensagem}</span>
                    <span>O que já foi gravado não se repete; o restante continua como alteração não salva.</span>
                </div>
            )}

            <div className="gt-corpo">
                <aside className="gt-paleta" aria-label="Turmas">
                    <div className="gt-paleta-topo">
                        <h2>Turmas</h2>
                        <span className="gt-contagem">{turmasVisiveis.length} de {turmas.length}</span>
                    </div>
                    <input type="search" className="gt-busca" placeholder="Buscar turma ou loja" aria-label="Buscar turma"
                        value={busca} onChange={(e) => setBusca(e.target.value)} />
                    <label className="gt-check">
                        <input type="checkbox" checked={soSemGrupo} onChange={(e) => setSoSemGrupo(e.target.checked)} />
                        Só turmas sem grupo
                    </label>
                    <ul className="gt-lista-turmas">
                        {turmasVisiveis.map((t) => {
                            const dela = gruposDaTurma[t.id] || [];
                            return (
                                <li key={t.id}
                                    className={`gt-chip gt-chip-turma${arrastando?.tipo === 'turma' && arrastando.id === t.id ? ' gt-chip-arrastado' : ''}`}
                                    draggable onDragStart={(e) => iniciarArrasto(e, 'turma', t.id)} onDragEnd={terminarArrasto}>
                                    <span className="gt-chip-dots" aria-hidden="true">
                                        {dela.length === 0 ? <span className="gt-bolinha gt-bolinha-vazia" /> :
                                            dela.map((g) => <Bolinha key={g.id} cor={g.color} titulo={g.name} />)}
                                    </span>
                                    <span className="gt-chip-texto">
                                        <span className="gt-chip-nome">{t.name}</span>
                                        <span className="gt-chip-sub">
                                            {dela.length ? `em ${dela.map((g) => g.name).join(', ')}` : 'sem grupo'}
                                        </span>
                                        <ProgramaTag programa={t.program} />
                                    </span>
                                    <button type="button" className="gt-icone-btn" disabled={rasc.length === 0}
                                        aria-label={`Colocar a turma ${t.name} em um grupo`}
                                        onClick={() => setDlg({ tipo: 'turma-em-grupo', turma: t, destino: '' })}>+</button>
                                </li>
                            );
                        })}
                        {!loading && turmasVisiveis.length === 0 && <li className="gt-vazio">Nenhuma turma encontrada</li>}
                    </ul>
                </aside>

                <main className="gt-quadro" aria-label="Grupos">
                    <div className="gt-quadro-topo">
                        <h2>Grupos <Info texto={TEXTO_QUADRO} rotulo="Como arrastar e soltar" /></h2>
                        {loading && <span className="gt-contagem">Carregando…</span>}
                    </div>

                    {!loading && raizes.length === 0 && (
                        <div className="gt-vazio-quadro">
                            <p>Nenhum grupo ainda.</p>
                            <button type="button" className="gt-btn gt-btn-primario" onClick={() => novoGrupo(null)}>Criar o primeiro grupo</button>
                        </div>
                    )}

                    <div className="gt-cartoes">
                        {renderLacuna(null, 0)}
                        {raizes.map((g, i) => (
                            <React.Fragment key={g.id}>
                                {renderCartao(g, i, raizes.length)}
                                {renderLacuna(null, i + 1)}
                            </React.Fragment>
                        ))}
                    </div>

                    {arrastando?.tipo === 'grupo' && (
                        <div className={`gt-raiz${alvo === 'raiz' ? ' gt-raiz-sobre' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setAlvo('raiz'); }}
                            onDragLeave={() => setAlvo((a) => (a === 'raiz' ? null : a))}
                            onDrop={(e) => {
                                e.preventDefault();
                                const id = arrastoRef.current?.id; terminarArrasto();
                                if (id != null) mover(id, null, null);
                            }}>
                            Solte aqui para deixar no nível principal
                        </div>
                    )}
                </main>
            </div>

            {sujo && (
                <div className="gt-barra" role="region" aria-label="Alterações não salvas">
                    <span className="gt-barra-texto">
                        <span className="gt-barra-ponto" aria-hidden="true" />
                        {mud.total} {mud.total === 1 ? 'alteração não salva' : 'alterações não salvas'}
                    </span>
                    <span className="gt-barra-acoes">
                        <button type="button" className="gt-btn" disabled={ocupado} onClick={() => setDlg({ tipo: 'descartar' })}>Descartar</button>
                        <button type="button" className="gt-btn gt-btn-primario" disabled={ocupado} onClick={abrirResumo}>Salvar</button>
                    </span>
                </div>
            )}

            {dlg?.tipo === 'grupo' && (
                <Dialogo titulo={dlg.form.id ? 'Editar grupo' : (dlg.form.parent_id != null ? 'Novo subgrupo' : 'Novo grupo')}
                    rotuloConfirmar="Aplicar ao rascunho"
                    confirmarHabilitado={!!dlg.form.name.trim()}
                    onCancelar={() => setDlg(null)} onConfirmar={salvarGrupoLocal}>
                    <div className="gt-form">
                        {dlg.form.parent_id != null && !dlg.form.id && porId[dlg.form.parent_id] && (
                            <p className="gt-form-nota">Dentro de <strong>{porId[dlg.form.parent_id].name}</strong></p>
                        )}
                        <label htmlFor="gt-f-nome">Nome</label>
                        <input id="gt-f-nome" type="text" value={dlg.form.name} maxLength={80}
                            onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, name: e.target.value } })} />
                        <span className="gt-rotulo" id="gt-f-cor-r">Cor</span>
                        <div className="gt-cores" role="radiogroup" aria-labelledby="gt-f-cor-r">
                            {PALETA.map((p) => (
                                <button type="button" key={p.cor} role="radio" aria-checked={dlg.form.color === p.cor}
                                    aria-label={p.nome} title={p.nome}
                                    className={`gt-cor${dlg.form.color === p.cor ? ' gt-cor-sel' : ''}`}
                                    style={{ background: p.cor }}
                                    onClick={() => setDlg({ ...dlg, form: { ...dlg.form, color: p.cor } })} />
                            ))}
                        </div>
                        <div className="gt-rotulo-linha">
                            <label htmlFor="gt-f-tags">Tags</label>
                            <Info texto={TEXTO_TAGS} rotulo="Sobre as tags" />
                        </div>
                        <input id="gt-f-tags" type="text" value={dlg.form.tags} placeholder="Ex.: loja, própria"
                            onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, tags: e.target.value } })} />

                        <button type="button" className="gt-avancado-btn" aria-expanded={dlg.form.avancado} aria-controls="gt-avancado"
                            onClick={() => setDlg({ ...dlg, form: { ...dlg.form, avancado: !dlg.form.avancado } })}>
                            <svg className={`gt-avancado-seta${dlg.form.avancado ? ' gt-avancado-seta-aberta' : ''}`} width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                                <path d="M4 2.5 8 6 4 9.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Opções avançadas
                            {(dlg.form.salesman_visibility || dlg.form.min_active || dlg.form.allow_mixed_programs || dlg.form.allow_mixed_phases) && <span className="gt-avancado-ponto" title="Há valores próprios nesta seção" />}
                        </button>
                        {dlg.form.avancado && (
                            <div id="gt-avancado" className="gt-avancado" role="group" aria-label="Opções avançadas">
                                <div className="gt-rotulo-linha">
                                    <label htmlFor="gt-f-visib">Nome do vendedor no ranking</label>
                                    <Info texto={TEXTO_VISIB} rotulo="Sobre o nome do vendedor no ranking" />
                                </div>
                                <select id="gt-f-visib" value={dlg.form.salesman_visibility}
                                    onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, salesman_visibility: e.target.value } })}>
                                    <option value="">Herdar do grupo pai (padrão)</option>
                                    <option value="full">Nome completo</option>
                                    <option value="initials">Só iniciais</option>
                                    <option value="anonymous">Anônimo</option>
                                </select>
                                <div className="gt-rotulo-linha">
                                    <label htmlFor="gt-f-minimo">Mínimo de vendedores para mostrar o número</label>
                                    <Info texto={TEXTO_MINIMO} rotulo="Sobre o mínimo de vendedores" />
                                </div>
                                <input id="gt-f-minimo" type="number" min="1" step="1" inputMode="numeric" value={dlg.form.min_active}
                                    placeholder="Vazio = herda (padrão da rede: 2)"
                                    onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, min_active: e.target.value } })} />
                                <div className="gt-rotulo-linha gt-interruptor">
                                    <input id="gt-f-programas" type="checkbox" checked={!!dlg.form.allow_mixed_programs}
                                        onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, allow_mixed_programs: e.target.checked } })} />
                                    <label htmlFor="gt-f-programas">Permitir programas diferentes</label>
                                    <Info texto={TEXTO_PROGRAMAS} rotulo="Sobre programas diferentes" />
                                </div>
                                <div className="gt-rotulo-linha gt-interruptor gt-interruptor-seguinte">
                                    <input id="gt-f-fases" type="checkbox" checked={!!dlg.form.allow_mixed_phases}
                                        onChange={(e) => setDlg({ ...dlg, form: { ...dlg.form, allow_mixed_phases: e.target.checked } })} />
                                    <label htmlFor="gt-f-fases">Permitir fases diferentes</label>
                                    <Info texto={TEXTO_FASES} rotulo="Sobre fases diferentes" />
                                </div>
                            </div>
                        )}
                    </div>
                </Dialogo>
            )}

            {dlg?.tipo === 'mover' && (
                <Dialogo titulo={`Mover "${dlg.grupo.name}"`} rotuloConfirmar="Mover"
                    onCancelar={() => setDlg(null)}
                    onConfirmar={() => {
                        const novo = dlg.destino === '' ? null : (Number.isNaN(Number(dlg.destino)) ? dlg.destino : Number(dlg.destino));
                        const g = dlg.grupo;
                        setDlg(null);
                        mover(g.id, novo, null);
                    }}>
                    <div className="gt-form">
                        <label htmlFor="gt-m-destino">Colocar dentro de</label>
                        <select id="gt-m-destino" value={dlg.destino} onChange={(e) => setDlg({ ...dlg, destino: e.target.value })}>
                            <option value="">Nível principal</option>
                            {rasc.filter((g) => g.id !== dlg.grupo.id && !descendentesEm(rasc, dlg.grupo.id).has(g.id)).map((g) => (
                                <option key={g.id} value={g.id}>{'— '.repeat(Math.max(0, profundidadeEm(rasc, g.id) - 1))}{g.name}</option>
                            ))}
                        </select>
                    </div>
                </Dialogo>
            )}

            {dlg?.tipo === 'turma-em-grupo' && (
                <Dialogo titulo={`Colocar "${dlg.turma.name}" em um grupo`} rotuloConfirmar="Colocar"
                    confirmarHabilitado={dlg.destino !== ''}
                    onCancelar={() => setDlg(null)}
                    onConfirmar={() => {
                        const g = Number.isNaN(Number(dlg.destino)) ? dlg.destino : Number(dlg.destino);
                        const t = dlg.turma.id;
                        setDlg(null);
                        incluir(t, g);
                    }}>
                    <div className="gt-form">
                        <label htmlFor="gt-t-destino">Grupo</label>
                        <select id="gt-t-destino" value={dlg.destino} onChange={(e) => setDlg({ ...dlg, destino: e.target.value })}>
                            <option value="">Escolha…</option>
                            {rasc.filter((g) => !g.cohorts.includes(dlg.turma.id)).map((g) => (
                                <option key={g.id} value={g.id}>{'— '.repeat(Math.max(0, profundidadeEm(rasc, g.id) - 1))}{g.name}</option>
                            ))}
                        </select>
                    </div>
                </Dialogo>
            )}

            {dlg?.tipo === 'apagar' && (
                <Dialogo titulo="Apagar grupo" perigo rotuloConfirmar="Apagar"
                    confirmarHabilitado={filhosDe(dlg.grupo.id).length === 0}
                    onCancelar={() => setDlg(null)} onConfirmar={apagarLocal}>
                    <p>
                        Apagar <strong>{dlg.grupo.name}</strong>?
                        {dlg.grupo.cohorts.length > 0 && ` As ${dlg.grupo.cohorts.length} turma(s) dele saem do grupo (continuam existindo).`}
                        {' '}Só é gravado quando você clicar em Salvar.
                    </p>
                    {filhosDe(dlg.grupo.id).length > 0 && (
                        <p className="gt-form-nota">Este grupo tem subgrupos. Mova ou apague os subgrupos primeiro.</p>
                    )}
                </Dialogo>
            )}

            {dlg?.tipo === 'resumo' && (
                <Dialogo titulo="Confirmar alterações" rotuloConfirmar="Confirmar e salvar" rotuloOcupado={progresso || 'Salvando…'}
                    rotuloCancelar="Cancelar" ocupado={ocupado}
                    onCancelar={() => { if (!ocupado) setDlg(null); }} onConfirmar={confirmarSalvar}>
                    <Resumo />
                </Dialogo>
            )}

            {dlg?.tipo === 'descartar' && (
                <Dialogo titulo="Descartar alterações?" perigo rotuloConfirmar="Descartar" rotuloCancelar="Voltar"
                    onCancelar={() => setDlg(null)} onConfirmar={descartar}>
                    <p>As {mud.total} alteração(ões) não salvas serão perdidas e a tela volta ao que está gravado.</p>
                </Dialogo>
            )}

            {dlg?.tipo === 'trocar-empresa' && (
                <Dialogo titulo="Alterações não salvas" perigo rotuloConfirmar="Trocar e descartar" rotuloCancelar="Continuar editando"
                    onCancelar={() => setDlg(null)}
                    onConfirmar={() => { const v = dlg.valor; setDlg(null); setSubdomain(v); }}>
                    <p>Você tem alterações não salvas. Se trocar de empresa agora, elas serão perdidas.</p>
                </Dialogo>
            )}

            {blocker.state === 'blocked' && (
                <Dialogo titulo="Alterações não salvas" perigo rotuloConfirmar="Sair sem salvar" rotuloCancelar="Continuar editando"
                    onCancelar={() => blocker.reset()}
                    onConfirmar={() => { saindoRef.current = true; blocker.proceed(); }}>
                    <p>Você tem alterações não salvas. Se sair agora, elas serão perdidas.</p>
                </Dialogo>
            )}

            {dlg?.tipo === 'historico' && (
                <Dialogo titulo={`Histórico de "${dlg.grupo.name}"`} rotuloConfirmar="Fechar" rotuloCancelar="Fechar"
                    onCancelar={() => setDlg(null)} onConfirmar={() => setDlg(null)}>
                    {dlg.movimentos.length === 0 ? <p>Sem movimentos.</p> : (
                        <ul className="gt-historico">
                            {dlg.movimentos.map((m) => {
                                const turma = m.cohort_id != null ? (turmaPorId[m.cohort_id]?.name || `turma #${m.cohort_id}`) : null;
                                return (
                                    <li key={m.id}>
                                        <Bolinha cor={dlg.grupo.color} />
                                        <span className="gt-hist-data">{dataBR(m.effective_on)}</span>
                                        <span>
                                            {ROTULO_MOVIMENTO[m.kind] || m.kind}
                                            {turma && <>: <strong>{turma}</strong></>}
                                            {m.kind === 'group_moved' && <> de {m.from_parent_id == null ? 'nível principal' : (baseNome(base, m.from_parent_id))} para {m.to_parent_id == null ? 'nível principal' : (baseNome(base, m.to_parent_id))}</>}
                                            {m.kind === 'group_created' && m.to_parent_id != null && <> dentro de {baseNome(base, m.to_parent_id)}</>}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Dialogo>
            )}
        </div>
    );
}

const baseNome = (base, id) => (base.find((g) => g.id === id) || {}).name || `grupo #${id}`;

export default GruposDeTurmas;
